import { useState, useEffect } from 'react';
import DatePicker from '@oncolens/oncolens-ui/lib/components/FormControls/DatePicker';
import FormAccordion from '@oncolens/oncolens-ui/lib/components/FormControls/FormAccordion';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import MultiListbox from '@oncolens/oncolens-ui/lib/components/FormControls/MultiListbox';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import Combobox, {
  ComboboxItem,
} from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import { FaTrash } from 'react-icons/fa';
import shallow from 'zustand/shallow';
import { EpisodeForCreationDto } from '../dtos/Episode';
import { createEpisode, updateEpisode } from '../hooks/useEpisode';
import useRequestFormStore from '../store';
import { mapConfig } from '../domain/Disease';
import MoveToDiseaseHistoryModal from '../components/MoveToDiseaseHistoryModal';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import { usePostCaseConfigs } from '../hooks/PostCaseUtilities';
import RiskFactors from '../RiskFactors/RiskFactors';
import StagingForm from '../Staging/StagingForm';
import DeleteDiseaseEpisodeModal from './DeleteDiseaseEpisodeModal';
import { useQueryParameters } from '@/hooks/useQueryParameters';
import { metastasisId } from '../configs/consts';

interface DiseaseEpisodeProps {
  id: string;
  diseaseId: string;
  isTarget: boolean;
  isSaved: boolean;
  isCancerTypeLateral: boolean;
  diagnosisStatus: string;
}

const DiseaseEpisode = ({
  id,
  diseaseId,
  isTarget,
  isSaved,
  isCancerTypeLateral,
  diagnosisStatus,
}: DiseaseEpisodeProps) => {
  const formKey = `diseases.${diseaseId}.episodes.${id}`;
  const configs = usePostCaseConfigs([
    'behavior',
    'setting',
    'recurrence',
    'recurrence_areas',
    'areas_of_metastasis',
  ]);
  const statuses = [
    { label: 'Initial', value: 'Initial' },
    { label: 'Progression', value: 'Progression' },
    { label: 'Recurrence', value: 'Recurrence' },
    { label: 'Unknown', value: 'Unknown' },
  ];
  const [accordionState, setAccordionState] = useState({
    isStagingOpen: false,
    isRiskFactorsOpen: false,
  });
  const [isDateAutoPopulated, setIsDateAutoPopulated] = useState(false);

  const [updateField, updateStore, getValues, getValue, isDirty] = useRequestFormStore(
    (store) => [
      store.updateField,
      store.updateStore,
      store.getValues,
      store.getValue,
      store.isDirty,
    ],
    shallow
  );
  const initialDiagnosisDate = getValue(`diseases.${diseaseId}.initialDiagnosisDate`);
  const initialDiagnosisDateDirty = isDirty(`diseases.${diseaseId}.initialDiagnosisDate`);
  const query = useQueryParameters();
  const isInAutosave = query.has('patientId') && query.has('eventId');
  const handleDelete = () => {
    updateStore((state) => {
      delete state.diseases[diseaseId].episodes[id];
      state.markClean(formKey);
    });
  };
  const episodeDate = getValue(`diseases.${diseaseId}.episodes.${id}.episodeDate`);
  const showDateTooltip = isDateAutoPopulated || !episodeDate;

  useEffect(() => {
    // update episode date if initial date changed in disease and episode is initial status
    if (
      getValue(`diseases.${diseaseId}.episodes.${id}.diagnosisStatus`) === 'Initial' &&
      initialDiagnosisDate &&
      initialDiagnosisDateDirty
    ) {
      updateField(`diseases.${diseaseId}.episodes.${id}.episodeDate`, initialDiagnosisDate, {
        markDirty: false,
      });
      setIsDateAutoPopulated(true);
    }
  }, [updateField, getValue, diseaseId, id, initialDiagnosisDate, initialDiagnosisDateDirty]);

  return (
    <div className="grid w-full grid-cols-2 gap-3 p-3 pb-4 mb-3 bg-white border border-gray-300 rounded-md shadow-md sm:grid-cols-8">
      <div className="grid grid-cols-2 col-span-2 pb-2 border-b border-gray-300 sm:col-span-8">
        <EpisodeHeader isTarget={isTarget} diseaseId={diseaseId} episodeId={id} />
        {/* {isTarget && isSaved ? (
          <MoveToDiseaseHistoryModal
            diseaseId={diseaseId}
            isCancerTypeLateral={isCancerTypeLateral}
          />
        ) : null} */}
        {!isTarget ? (
          isSaved ? (
            <DeleteDiseaseEpisodeModal diseaseId={diseaseId} episodeId={id} />
          ) : (
            <FaTrash
              onClick={handleDelete}
              className="box-content col-start-2 p-2 ml-auto text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
            />
          )
        ) : null}
      </div>
      <FormField
        name={`${formKey}.diagnosisStatus`}
        store={useRequestFormStore}
        validationOptions={{
          valueProvider: getValues,
        }}
      >
        {({ value, onChange, errorMessage }) => {
          const selectedItem = statuses.find((status) => status.value === value) || null;

          return (
            <Combobox
              id="combobox-status"
              htmlName="combobox-status"
              label="Status"
              data={statuses}
              value={selectedItem as ComboboxItem}
              error={errorMessage}
              required
              onChange={(changedValue: any) => {
                const { value: newValue } = changedValue;

                if (newValue === 'Recurrence' && value !== 'Recurrence') {
                  updateField(`diseases.${diseaseId}.episodes.${id}.areasOfMetastasis`, [], {
                    markDirty: false,
                  });
                  updateField(
                    `diseases.${diseaseId}.episodes.${id}.areasOfMetastasisOtherComments`,
                    null,
                    { markDirty: false }
                  );
                } else if (newValue !== 'Recurrence' && value === 'Recurrence') {
                  updateField(`diseases.${diseaseId}.episodes.${id}.areasOfRecurrence`, [], {
                    markDirty: false,
                  });
                  updateField(
                    `diseases.${diseaseId}.episodes.${id}.areasOfRecurrenceOtherComments`,
                    null,
                    { markDirty: false }
                  );
                }

                if (
                  newValue === 'Initial' &&
                  value !== 'Initial' &&
                  getValue(`diseases.${diseaseId}.initialDiagnosisDate`)
                ) {
                  updateField(
                    `diseases.${diseaseId}.episodes.${id}.episodeDate`,
                    getValue(`diseases.${diseaseId}.initialDiagnosisDate`),
                    { markDirty: false }
                  );
                  setIsDateAutoPopulated(true);
                } else if (isDateAutoPopulated) {
                  updateField(`diseases.${diseaseId}.episodes.${id}.episodeDate`, null);
                  setIsDateAutoPopulated(false);
                }

                onChange(newValue);
              }}
              wrapperProps={{ className: 'col-span-2 sm:col-span-3' }}
            />
          );
        }}
      </FormField>
      <FormField
        name={`${formKey}.episodeDate`}
        store={useRequestFormStore}
        validationOptions={{
          valueProvider: getValues,
        }}
      >
        {({ value, onChange, errorMessage }) => (
          <DatePicker
            label={'Date'}
            id={'episodeDate'}
            htmlName={'episodeDate'}
            required={true}
            error={errorMessage}
            selected={value}
            setSelected={(newValue: any) => {
              if (
                isDateAutoPopulated &&
                newValue !== getValue(`diseases.${diseaseId}.initialDiagnosisDate`)
              ) {
                setIsDateAutoPopulated(false);
              }
              onChange(newValue);
            }}
            wrapperProps={{
              className: 'col-span-2 sm:col-span-3 sm:col-start-5',
            }}
            inputProps={{ maxDate: new Date() }}
            tooltip={
              showDateTooltip
                ? {
                    text: isDateAutoPopulated
                      ? 'This date was auto-populated from the Date of Initial Diagnosis Field'
                      : 'Please provide the date of the pathology or radiology report',
                  }
                : undefined
            }
          />
        )}
      </FormField>
      <FormField
        name={`${formKey}.cancerSettingId`}
        store={useRequestFormStore}
        validationOptions={{
          valueProvider: getValues,
        }}
      >
        {({ value, onChange, errorMessage }) => (
          <Listbox
            label="Setting"
            required={isTarget}
            data={mapConfig(configs?.setting)}
            error={errorMessage}
            selected={value}
            setSelected={onChange}
            wrapperProps={{ className: 'col-span-2 sm:col-span-4' }}
          />
        )}
      </FormField>

      <FormField
        name={`${formKey}.cancerBehaviorId`}
        store={useRequestFormStore}
        validationOptions={{
          valueProvider: getValues,
        }}
      >
        {({ value, onChange, errorMessage }) => (
          <Listbox
            label="Behavior"
            required={isTarget}
            data={mapConfig(configs?.behavior)}
            error={errorMessage}
            selected={value}
            setSelected={onChange}
            wrapperProps={{ className: 'col-span-2 sm:col-span-4' }}
          />
        )}
      </FormField>
      <FormField
        name={`${formKey}.laterality`}
        store={useRequestFormStore}
        validationOptions={{
          valueProvider: getValues,
        }}
      >
        {({ value, onChange, errorMessage }) => (
          <Listbox
            label="Laterality"
            data={
              isCancerTypeLateral
                ? [
                    { value: 'Left', label: 'Left' },
                    { value: 'Right', label: 'Right' },
                    { value: 'Bilateral', label: 'Bilateral' },
                    { value: 'Midline', label: 'Midline' },
                  ]
                : [{ label: 'Not Applicable', value: null }]
            }
            error={errorMessage}
            selected={value}
            setSelected={onChange}
            disabled={!isCancerTypeLateral}
            wrapperProps={{ className: 'col-span-1 sm:col-span-2' }}
          />
        )}
      </FormField>
      {diagnosisStatus !== 'Recurrence' ? (
        <FormField
          name={`${formKey}.areasOfMetastasis`}
          store={useRequestFormStore}
          validationOptions={{
            valueProvider: getValues,
          }}
        >
          {({ value, onChange, errorMessage }) => {
            const otherOption = configs?.areas_of_metastasis?.find(
              (area) => area.label === 'Other'
            );
            const otherId = otherOption?.value || 'other-option-not-found'; // dont show entire field until config is there?
            const showOther = value && value.indexOf(otherId) >= 0;
            return (
              <div className="col-span-1 sm:col-span-2">
                <MultiListbox
                  label="Areas of Metastasis"
                  data={mapConfig(configs?.areas_of_metastasis)}
                  error={errorMessage}
                  selected={value}
                  required={
                    getValues().diseases[diseaseId].episodes[id].cancerSettingId === metastasisId
                  }
                  setSelected={(selected) => {
                    // reset areasOfMetastasisOtherComments if other is no longer selected
                    const isOtherSelected = Array.isArray(selected)
                      ? (selected as string[]).indexOf(otherId) >= 0
                      : selected === otherId;
                    if (!isOtherSelected && getValue(`${formKey}.areasOfMetastasisOtherComments`)) {
                      updateField(
                        `diseases.${diseaseId}.episodes.${id}.areasOfMetastasisOtherComments`,
                        null,
                        { markDirty: false }
                      );
                    }
                    onChange(selected);
                  }}
                />
                {showOther && (
                  <FormField
                    name={`${formKey}.areasOfMetastasisOtherComments`}
                    store={useRequestFormStore}
                    validationOptions={{
                      valueProvider: getValues,
                    }}
                  >
                    {({ value: valueOther, onChange: onChangeOther }) => (
                      <TextInput
                        id="other-areas-of-metastasis"
                        value={valueOther}
                        onChange={onChangeOther}
                        label=""
                        htmlName="other-areas-of-metastasis"
                        type="text"
                        placeholder="Specify Metastatic Site(s)"
                      />
                    )}
                  </FormField>
                )}
              </div>
            );
          }}
        </FormField>
      ) : null}
      {diagnosisStatus === 'Recurrence' ? (
        <>
          <FormField
            name={`${formKey}.recurrenceExtentId`}
            store={useRequestFormStore}
            validationOptions={{
              valueProvider: getValues,
            }}
          >
            {({ value, onChange, errorMessage }) => (
              <Listbox
                label="Recurrent With Extent"
                data={mapConfig(configs?.recurrence)}
                error={errorMessage}
                selected={value}
                setSelected={onChange}
                wrapperProps={{ className: 'col-span-1 sm:col-span-2' }}
              />
            )}
          </FormField>
          <FormField
            name={`${formKey}.areasOfRecurrence`}
            store={useRequestFormStore}
            validationOptions={{
              valueProvider: getValues,
            }}
          >
            {({ value, onChange, errorMessage }) => {
              const otherOption = configs?.recurrence_areas?.find((area) => area.label === 'Other');
              const otherId = otherOption?.value || 'other-option-not-found'; // dont show entire field until config is there?
              const showOther = value && value.indexOf(otherId) >= 0;
              return (
                <div className="col-span-1 sm:col-span-2">
                  <MultiListbox
                    label="Areas of Recurrence"
                    data={mapConfig(configs?.recurrence_areas)}
                    error={errorMessage}
                    selected={value}
                    setSelected={(selected) => {
                      // reset areasOfMetastasisOtherComments if other is no longer selected
                      const isOtherSelected = Array.isArray(selected)
                        ? (selected as string[]).indexOf(otherId) >= 0
                        : selected === otherId;
                      if (
                        !isOtherSelected &&
                        getValue(`${formKey}.areasOfRecurrenceOtherComments`)
                      ) {
                        updateField(
                          `diseases.${diseaseId}.episodes.${id}.areasOfRecurrenceOtherComments`,
                          null,
                          { markDirty: false }
                        );
                      }
                      onChange(selected);
                    }}
                  />
                  {showOther && (
                    <FormField
                      name={`${formKey}.areasOfRecurrenceOtherComments`}
                      store={useRequestFormStore}
                      validationOptions={{
                        valueProvider: getValues,
                      }}
                    >
                      {({ value: valueOther, onChange: onChangeOther }) => (
                        <TextInput
                          id="other-areas-of-recurrence"
                          value={valueOther}
                          onChange={onChangeOther}
                          label=""
                          htmlName="other-areas-of-recurrence"
                          type="text"
                          placeholder="Specify Recurrence Site(s)"
                        />
                      )}
                    </FormField>
                  )}
                </div>
              );
            }}
          </FormField>
        </>
      ) : null}
      <div className="col-span-2 sm:col-span-8">
        <FormAccordion
          title="Staging"
          titleId="staging"
          isOpen={accordionState.isStagingOpen}
          setIsOpen={() =>
            setAccordionState((state) => ({
              ...state,
              isStagingOpen: !state.isStagingOpen
            }))
          }
          fontSize="small"
        >
          <StagingForm diseaseId={diseaseId} episodeId={id} isInAutosave={isInAutosave} />
        </FormAccordion>
        <FormAccordion
          title="Risk & Prognostic Factors"
          titleId="riskFactors"
          isOpen={accordionState.isRiskFactorsOpen}
          setIsOpen={() =>
            setAccordionState((state) => ({
              ...state,
              isRiskFactorsOpen: !state.isRiskFactorsOpen,
            }))
          }
          fontSize="small"
        >
          <RiskFactors
            diseaseId={diseaseId}
            episodeId={id}
            isEpisodeSaved={isSaved}
            isInAutosave={isInAutosave}
          />
        </FormAccordion>
      </div>
    </div>
  );
};

interface EpisodeHeaderProps {
  isTarget: boolean;
  episodeId: string;
  diseaseId: string;
}

const EpisodeHeader = ({ isTarget, episodeId, diseaseId }: EpisodeHeaderProps) => {
  const [date, status] = useRequestFormStore(
    (store) => [
      store.diseases[diseaseId]?.episodes[episodeId]?.episodeDate,
      store.diseases[diseaseId]?.episodes[episodeId]?.diagnosisStatus,
    ],
    shallow
  );

  return (
    <h3 className="mt-1 font-bold text-gray-800">
      {isTarget
        ? 'Current Disease'
        : `History${date || status ? ':' : ''} ${date ? date.toLocaleDateString() : ''}${
            date && status ? ' - ' : ''
          }${status || ''}`}
    </h3>
  );
};

DiseaseEpisode.save = async (data: EpisodeForCreationDto) => {
  return createEpisode(data);
};

DiseaseEpisode.createOrUpdate = async (diseaseId: string, episodeId: string) => {
  const { updateStore, diseases } = useRequestFormStore.getState();
  const episode = diseases[diseaseId].episodes[episodeId];

  if (episode.isSaved) {
    return updateEpisode(episodeId, {
      ...episode,
    });
  } else {
    return createEpisode({
      ...episode,
    }).then((result) => {
      updateStore((state) => {
        delete state.diseases[diseaseId].episodes[episodeId];
        state.diseases[diseaseId].episodes[result.id] = result;
        state.diseases[diseaseId].episodes[result.id].isSaved = true;
      });
    });
  }
};

export { DiseaseEpisode as default };
